import React, { FC, useCallback, useMemo } from "react";
import { IconToggle, Popover } from "@evergis/ui";
import {
  AttributeType,
  AttributeFormatDc,
  GeometryType,
  StringSubType,
  AttributeConfigurationDc,
  LayerReferenceConfigurationDc,
  AttributeSelectorType,
} from "@evergis/api";
import { useTranslation } from "react-i18next";
import isValidLayerReference from "../LayerReferences/isValidLayerReference";
import { AttributeDescription } from "./AttributeDescription";
import { AttributeSelectorTypeForm } from "./AttributeSelectorTypeForm";
import { getSubTypeDisplayText } from "./FormStateSettings";

import { CustomBlank, FormatInput, FormatInputRow } from "./styled";
import { FormatSettings } from "./FormatSettings";
import { useToggle } from "../../../../../hooks/useToggle";
import { UpdateAttribute } from "../../../../../hooks/attributes/useAttributes";
import { useAttributeFormat } from "../hooks/useAttributeFormat";
import { LayerAttribute } from "../../../EditResource/components/Layer/LayerAttributeItem";
import { EditGeometryType } from "../../../../../evergis/core/attributes/geometryTypes";

export type AttributeFormatProps = {
  type: AttributeType;
  id: string;
  subType?: StringSubType;
  geometryType: GeometryType | EditGeometryType;
  updateAttribute: UpdateAttribute<LayerAttribute>;
  stringFormat?: AttributeFormatDc;
  isCalculated?: boolean;
  withoutInput?: boolean;
  description?: string;
  layerReferences?: LayerReferenceConfigurationDc[];
  attributeConfiguration?: AttributeConfigurationDc;
};

// `AttributeType.DateTime` is temporarily disabled due to PUB-3340
const FORMAT_ENABLED_TYPES = [
  AttributeType.Point,
  AttributeType.Double,
  AttributeType.Int32,
  AttributeType.Int64,
  AttributeType.String,
];

// eslint-disable-next-line max-lines-per-function
export const AttributeFormat: FC<AttributeFormatProps> = ({
  type,
  subType,
  geometryType,
  id,
  updateAttribute,
  stringFormat,
  isCalculated,
  withoutInput,
  description,
  attributeConfiguration,
  layerReferences,
}) => {
  const { t, i18n } = useTranslation("dialogs");
  const [isMenuOpen, toggleMenu] = useToggle(false);
  const attributeSelectorType = attributeConfiguration?.attributeSelectorType;
  const withAttributeSelector = layerReferences && layerReferences.filter(isValidLayerReference).length > 0;

  const {
    formState,
    currentFormat,
    onChangeFormatValue,
    onChangeFormatTime,
    onChangeNumberInput,
    onChangeFormatLabel,
    toggleSelected,
  } = useAttributeFormat(type, id, geometryType, updateAttribute, stringFormat, isCalculated);

  const formatTypes = useMemo(
    () => (subType ? FORMAT_ENABLED_TYPES : FORMAT_ENABLED_TYPES.filter(value => value !== AttributeType.String)),
    [subType],
  );

  const onChangeFormatSubType = useCallback(
    (newSubType: StringSubType) => {
      updateAttribute(id, {
        subType: newSubType,
      });
    },
    [updateAttribute, id],
  );

  const onChangeDescription = useCallback(
    (newDescription: string) => {
      updateAttribute(id, {
        description: newDescription,
      });
    },
    [updateAttribute, id],
  );

  const onChangeSelectorType = useCallback(
    (selectorType: AttributeSelectorType) => {
      updateAttribute(id, {
        attributeSelectorType: selectorType,
      });
    },
    [updateAttribute, id],
  );

  const onChangeReference = useCallback(
    (layerReferenceId: string[]) => {
      updateAttribute(id, {
        layerReferenceId,
      });
    },
    [updateAttribute, id],
  );

  const currentFormatText = useMemo(() => {
    if (attributeSelectorType === AttributeSelectorType.SelectFromHandBook) {
      return t("selectFromDictionary", { ns: "common" });
    }

    if (attributeSelectorType === AttributeSelectorType.ViewHandBook) {
      return t("relatedRecords", { ns: "common" });
    }

    return type !== AttributeType.String ? currentFormat : getSubTypeDisplayText(subType);
  }, [currentFormat, subType, attributeSelectorType, t, type]);

  return (
    <>
      {((i18n.language === "en" ? formatTypes : [...formatTypes, AttributeType.DateTime]).includes(type) && (
        <FormatInputRow>
          {!withoutInput && (
            <FormatInput
              width={"11.375rem"}
              label={`${t("dialogComponents.display")}:`}
              value={currentFormatText}
              readOnly
            />
          )}
          <Popover
            closeOnScroll={false}
            animateAppearance
            open={isMenuOpen}
            onRequestClose={toggleMenu}
            zIndex={9999}
            anchor={<IconToggle isSelected={isMenuOpen} kind={"settings"} onClick={toggleMenu} />}
          >
            <CustomBlank>
              <FormatSettings
                type={type}
                subType={subType}
                formState={formState}
                attributeSelectorType={attributeSelectorType}
                childrenBefore={
                  withAttributeSelector && (
                    <AttributeSelectorTypeForm
                      attributeType={type}
                      attributeConfiguration={attributeConfiguration}
                      layerReferences={layerReferences}
                      onChangeSelectorType={onChangeSelectorType}
                      onChangeReference={onChangeReference}
                    />
                  )
                }
                onChangeFormatValue={onChangeFormatValue}
                onChangeFormatTime={onChangeFormatTime}
                onChangeNumberInput={onChangeNumberInput}
                onChangeFormatLabel={onChangeFormatLabel}
                onChangeFormatSubType={onChangeFormatSubType}
                toggleSelected={toggleSelected}
              >
                <AttributeDescription description={description} onChangeDescription={onChangeDescription} />
              </FormatSettings>
            </CustomBlank>
          </Popover>
        </FormatInputRow>
      )) || <span style={{ width: withoutInput ? "3rem" : "14.375rem", flexShrink: 0 }} />}
    </>
  );
};
